import { Common, Company } from '@stockbit/ui';
import numeral from 'numeral';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { convertToSlug } from 'utils/general';
import { clevertapEvent } from 'utils/clevertap';
import { useSelector } from 'react-redux';
import { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { selectors as profileSelectors } from 'lib/entities/profile';
import companyUtils from 'utils/company';
import { Tooltip } from 'antd';
import IndicatorBar from 'global/Notation/IndicatorBar';
import CorpActionBar from 'global/CorpAction/CorpActionBar';
import { renderTags } from 'utils/tags';
import { ModalFollower } from 'global/Moderation/Modals';
import Image from 'global/Image';
import { TAB_LIST_NAME } from 'features/company/constants';
import useIsUserSyariah from 'global/TradingModal/hooks/useIsUserSyariah';
import If from '../../../global/ErrorHandlers/If';
import {
  CountryFlag,
  ImagePlaceholder,
  Symbol,
  CountryFlagContainer,
  NotationCountryFlagContainer,
  NotationContainer,
  Badges,
  CompanyName,
  CompanyNameContainer,
  CompanyTags,
  ImageWrapper,
  CompanyInfoWrapper,
  CompanyHeader,
  ExpandButtonToggleMobile,
  NotationCorpActionBarWrapper,
  FollowerWrapper,
  ButtonBuy,
  CompanyIdentity,
  ButtonContainer,
  ButtonContainerMobile,
  CountryFlagContainerMobile,
  ModalFollowing,
  MultiplierChip,
  MultiplierContainer,
  CatalogButton,
} from './styled';
import FollowCompanyButton from './FollowCompanyButton';

const { cleanSymbol, isReksaDana } = companyUtils;

const { Flex, Text } = Common;
const {
  TagCompany: { SymbolTag },
} = Company;

interface CompanyChartProps {
  symbol: string;
  company: any;
  isLoadingFollow?: boolean;
  isGuestCompany?: boolean;
  onClickLink?: (...args) => void;
  onClickFollow?: (...args) => void;
  onClickCorpAction?: (...args) => void;
  onClickNotation?: (...args) => void;
  onClickBuyButton?: (...args) => void;
  onExpandToggle?: (...args) => void;
}

const STATUS_LIST = {
  SUSPENDED: 'STATUS_SUSPENDED',
  DELISTED: 'STATUS_DELISTED',
};

function Header({
  symbol = '',
  company,
  isGuestCompany = false,
  onClickLink,
  onClickFollow,
  onClickCorpAction,
  onClickNotation,
  onClickBuyButton,
  onExpandToggle,
}: CompanyChartProps) {
  const {
    name,
    sector,
    followers,
    followed,
    icon_url,
    country = 'ID',
    notation = [],
    uma,
    corp_action = {},
    status,
    catalogs = [],
    exchange,
    type_company = '',
    id,
    tradeable,
    day_trade_info: dayTradeInfo,
  } = company;
  const { multiplier, is_show_multiplier: isShowMultiplier } =
    dayTradeInfo || {};
  const { data: profileData } = useSelector(profileSelectors);
  const { country: profileCountry = 'ID' } = profileData;

  const [isIconAvailable, setIsIconAvailable] = useState<boolean>(!!icon_url);
  const [isFollowerVisible, setIsFollowerVisible] = useState<boolean>(false);
  const [isFollowCompanyVisible, setIsFollowCompanyVisible] =
    useState<boolean>(false);
  const [isFollowing, setIsFollowing] = useState<boolean>(!!followed);
  const isNotationAvailable = !!notation.length;
  const isCorpActionAvailable = corp_action?.active;
  const isSuspended = status === STATUS_LIST.SUSPENDED;
  const isDelisted = status === STATUS_LIST.DELISTED;
  const isCompanyStock = true;
  const isTradeable = tradeable === 1;
  const POPUP_COUNTRY_FLAG_TEXT = `This stock is traded on ${exchange} exchange`;

  const buttonBuyRef = useRef<HTMLButtonElement>(null);
  const router = useRouter();
  const isUserSyariah = useIsUserSyariah();

  const showMultiplier = useMemo(
    () =>
      [parseInt(multiplier, 10) > 1, isShowMultiplier, !isUserSyariah].every(
        Boolean,
      ),
    [multiplier, isShowMultiplier, isUserSyariah],
  );

  // If not user Day Trade exclude catalog daytrade
  const filteredCatalogs = useMemo(
    () =>
      catalogs.filter(
        ({ company_symbol }) =>
          showMultiplier || company_symbol?.toLowerCase() !== 'daytrade',
      ),
    [catalogs, showMultiplier],
  );

  const onImgError = () => {
    setIsIconAvailable(false);
  };

  const formattedCountry = country.toLowerCase();
  const IMAGE_URL = `/img/flags/${formattedCountry}.png`;

  const generateCatalogUrl = (itemSector, itemSubSector) => {
    if (!itemSector) return '';

    if (sector && sector.toLowerCase() !== 'indeks') {
      return `/catalog/${convertToSlug(itemSector)}/${convertToSlug(
        itemSubSector,
      )}`;
    }

    return `/catalog/${convertToSlug(itemSubSector)}`;
  };

  const onClickCatalogsLink = (event, companySymbol: string) => {
    if (isGuestCompany) {
      event.preventDefault();
      onClickLink(true);
    } else {
      clevertapEvent('Company Action', {
        Action: 'emiten_group',
        Group: companySymbol,
      });
    }
  };

  const handleSuccessFollow = (following) => {
    setIsFollowing(following);
  };

  const handleToggleFollow = (following) => {
    if (isGuestCompany) {
      onClickFollow();
      return false;
    }
    handleSuccessFollow(following);
    return true;
  };

  const handleOpenModal = useCallback(() => {
    setIsFollowCompanyVisible(true);
  }, [isFollowCompanyVisible]);

  const handleCancelModal = useCallback(() => {
    setIsFollowCompanyVisible(false);
  }, [isFollowCompanyVisible]);

  const handleClickBuyButton = useCallback(() => {
    buttonBuyRef?.current?.click();
  }, [buttonBuyRef]);

  useEffect(() => {
    if (router?.query?.param?.[0] === TAB_LIST_NAME.BUY) {
      handleClickBuyButton();
    }
  }, [handleClickBuyButton, router?.query?.param]);

  const renderCatalogsLink = (catalogue) => {
    const subSectorObject = {
      'Properti & Real Estate': 'Properti dan Real Estate',
      Commodities: 'Commodity',
    };
    const companySymbol = catalogue.catalog_name || catalogue.company_symbol;
    const companySymbolAlias =
      subSectorObject[companySymbol] || catalogue.company_symbol;
    const companyType = catalogue.company_type;

    let catalogUrl = '';
    switch (companyType) {
      case 'sub_sector':
        catalogUrl = generateCatalogUrl(sector, companySymbolAlias);
        break;
      case 'listing-board':
        catalogUrl = generateCatalogUrl(
          companyType,
          convertToSlug(companySymbolAlias),
        );
        break;
      default:
        catalogUrl = `/catalog/${companyType}/${companySymbolAlias}`;
        break;
    }

    return (
      <Link href={catalogUrl} passHref>
        <CatalogButton
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => onClickCatalogsLink(e, companySymbol)}
        >
          {companySymbol}
        </CatalogButton>
      </Link>
    );
  };

  const renderSymbol = () => (
    <Symbol>{cleanSymbol(symbol.toUpperCase())}</Symbol>
  );

  const renderCountryFlag = () => (
    <CountryFlagContainer>
      <Tooltip
        className="desktop"
        placement="top"
        title={POPUP_COUNTRY_FLAG_TEXT}
      >
        <CountryFlag
          key="country-flag-desktop"
          src={IMAGE_URL}
          alt={`${country} flag`}
        />
      </Tooltip>
    </CountryFlagContainer>
  );

  const renderCountryFlagMobile = () => (
    <CountryFlagContainerMobile>
      <CountryFlag
        className="mobile"
        key="country-flag-mobile"
        src={IMAGE_URL}
        alt={`${country} flag`}
      />
    </CountryFlagContainerMobile>
  );

  const renderButtons = () => (
    <ButtonContainer>
      <FollowerWrapper as="span" onClick={() => setIsFollowerVisible(true)}>
        {numeral(followers).format('0,0')} followers
      </FollowerWrapper>
      <FollowCompanyButton isFollowed={isFollowing} onClick={handleOpenModal} />
      <If
        condition={
          isTradeable && !isReksaDana(type_company) && profileCountry === 'ID'
        }
      >
        <ButtonBuy
          data-cy="company-buy-button"
          onClick={onClickBuyButton}
          ref={buttonBuyRef}
        >
          <Text weight="bolder" align="center" color="inherit">
            Buy
          </Text>
        </ButtonBuy>
      </If>
    </ButtonContainer>
  );

  const renderButtonsMobile = () => (
    <ButtonContainerMobile>
      <FollowerWrapper as="span" onClick={() => setIsFollowerVisible(true)}>
        {numeral(followers).format('0,0')} followers
      </FollowerWrapper>
      <FollowCompanyButton isFollowed={isFollowing} onClick={handleOpenModal} />
      <If
        condition={
          isTradeable && !isReksaDana(type_company) && profileCountry === 'ID'
        }
      >
        <ButtonBuy onClick={onClickBuyButton}>
          <Text weight="bolder" align="center" color="inherit">
            Buy
          </Text>
        </ButtonBuy>
      </If>
    </ButtonContainerMobile>
  );

  const renderCompanyLogo = () => (
    <ImageWrapper align="center">
      <Flex align="center">
        {isIconAvailable ? (
          <img
            className="company-header-icon"
            src={icon_url}
            onError={onImgError}
            alt={symbol}
            width="54px"
            height="54px"
          />
        ) : (
          <ImagePlaceholder>
            <small>{!!symbol && symbol.toUpperCase().slice(0, 1)}</small>
          </ImagePlaceholder>
        )}
        {!!country && renderCountryFlagMobile()}
      </Flex>
      {renderButtonsMobile()}
    </ImageWrapper>
  );

  const renderMultiplier = () => (
    <MultiplierContainer>
      <MultiplierChip className="multiplier-chip">
        <Image
          src="/images/day-trade/lightning-filled-company.svg"
          alt=""
          height="10"
          width="10"
        />

        <Text size="12px" color="#8277F8" weight="bold">
          {`${multiplier}x`}
        </Text>
      </MultiplierChip>
    </MultiplierContainer>
  );

  const renderNotationCorpActionBadges = () => (
    <NotationContainer>
      {renderTags({ symbol, uma, notation, corp_action, name })}
    </NotationContainer>
  );

  const renderSuspendedDelistedBadges = () => (
    <>
      {isSuspended && (
        <Badges>
          <span>Suspended</span>
        </Badges>
      )}
      {isDelisted && (
        <Badges>
          <span>Delisted</span>
        </Badges>
      )}
    </>
  );

  const renderCompanyName = () => (
    <CompanyName>
      <h1>{name}</h1>
    </CompanyName>
  );

  const renderCompanyTags = () => (
    <CompanyTags>
      {filteredCatalogs.slice(0, 6).map(
        (catalogue) =>
          catalogue.show && (
            <span key={catalogue.company_symbol} className="catalogs">
              {renderCatalogsLink(catalogue)}
            </span>
          ),
      )}
    </CompanyTags>
  );

  const renderExpandButtonToggle = () => (
    <ExpandButtonToggleMobile onExpand={onExpandToggle} />
  );

  const renderNotationCorpActionBar = () => (
    <NotationCorpActionBarWrapper
      isCompanyStock={isCompanyStock}
      isBoth={isNotationAvailable && isCorpActionAvailable}
    >
      {isNotationAvailable && (
        <IndicatorBar
          symbol={symbol}
          notation={notation}
          uma={uma}
          name={name}
          isGuestCompanyStock={false}
          onClick={onClickNotation}
        />
      )}

      {isCorpActionAvailable && (
        <CorpActionBar
          symbol={symbol}
          corpAction={corp_action}
          isGuestCompanyStock={false}
          onClick={onClickCorpAction}
        />
      )}
    </NotationCorpActionBarWrapper>
  );

  // TODO: Should be fixed by SNE/V3 Rewrite
  const renderFollowerModal = () => (
    <ModalFollower
      symbol={symbol}
      companyId={id}
      visible={isFollowerVisible}
      onCancel={() => setIsFollowerVisible(false)}
    />
  );

  const renderFollowModal = () => (
    <ModalFollowing
      visible={isFollowCompanyVisible}
      onCancel={handleCancelModal}
      companyId={id}
      onFollow={handleToggleFollow}
    />
  );

  const renderCompanyHeader = () => (
    <CompanyHeader>
      <CompanyIdentity>
        <CompanyInfoWrapper $isIconAvailable={isIconAvailable}>
          {renderCompanyLogo()}
          <CompanyNameContainer>
            {renderSymbol()}
            <NotationCountryFlagContainer>
              {showMultiplier && renderMultiplier()}
              {renderNotationCorpActionBadges()}
              {!!country && renderCountryFlag()}
              {renderSuspendedDelistedBadges()}
            </NotationCountryFlagContainer>
            {renderCompanyName()}
            <If condition={profileCountry === 'ID'}>{renderCompanyTags()}</If>
          </CompanyNameContainer>
          {renderExpandButtonToggle()}
        </CompanyInfoWrapper>
        {renderButtons()}
      </CompanyIdentity>
      {renderNotationCorpActionBar()}
      {renderFollowerModal()}
      {renderFollowModal()}
    </CompanyHeader>
  );

  return renderCompanyHeader();
}

export default Header;
